import { useState, useEffect, useCallback, useRef } from 'react';

interface UseKioskActivityOptions {
  promptInterval?: number;
  responseTimeout?: number;
  onNoResponse: () => void;
  enabled?: boolean;
}

interface UseKioskActivityReturn {
  showPrompt: boolean;
  confirmActivity: () => void;
  remainingSeconds: number;
}

export const useKioskActivity = ({
  promptInterval = 120000, // 2 minutos
  responseTimeout = 15000, // 15 segundos
  onNoResponse,
  enabled = true
}: UseKioskActivityOptions): UseKioskActivityReturn => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(15);
  const promptTimerRef = useRef<NodeJS.Timeout>();
  const responseTimerRef = useRef<NodeJS.Timeout>();
  const countdownIntervalRef = useRef<NodeJS.Timeout>();
  const onNoResponseRef = useRef(onNoResponse);

  // Actualizar ref si cambia onNoResponse (sin causar re-ejecución del efecto)
  useEffect(() => {
    onNoResponseRef.current = onNoResponse;
  }, [onNoResponse]);

  const startPromptTimer = useCallback(() => {
    if (promptTimerRef.current) {
      clearTimeout(promptTimerRef.current);
    }

    console.log('⏰ Timer WebView iniciado - mensaje en 2 minutos');
    promptTimerRef.current = setTimeout(() => {
      console.log('⏰ Mostrando overlay "¿Sigues aquí?"');
      setShowPrompt(true);
      setRemainingSeconds(Math.floor(responseTimeout / 1000));

      // Iniciar countdown
      countdownIntervalRef.current = setInterval(() => {
        setRemainingSeconds(prev => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Timer de espera de respuesta
      responseTimerRef.current = setTimeout(() => {
        console.log('❌ Sin respuesta del usuario - ejecutando onNoResponse');
        setShowPrompt(false);
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
        onNoResponseRef.current();
      }, responseTimeout);
    }, promptInterval);
  }, [promptInterval, responseTimeout]);

  const confirmActivity = useCallback(() => {
    console.log('✅ Usuario confirmó actividad');
    setShowPrompt(false);
    
    // Limpiar timers
    if (responseTimerRef.current) {
      clearTimeout(responseTimerRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    // Reiniciar timer de prompt
    startPromptTimer();
  }, [startPromptTimer]);

  useEffect(() => {
    if (!enabled) {
      setShowPrompt(false);
      if (promptTimerRef.current) {
        clearTimeout(promptTimerRef.current);
      }
      if (responseTimerRef.current) {
        clearTimeout(responseTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      return;
    }

    startPromptTimer();

    return () => {
      if (promptTimerRef.current) {
        clearTimeout(promptTimerRef.current);
      }
      if (responseTimerRef.current) {
        clearTimeout(responseTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [enabled, startPromptTimer]);

  return {
    showPrompt,
    confirmActivity,
    remainingSeconds
  };
};
